
import IdentityForm from "../application/forms/IdentityForm.vue";
import ModernInput from "../application/forms/modernInput.vue";
import { Address, User } from "../../../../domain/entities/entities";
import InputPassword from "../application/forms/InputPassword.vue";
import store from "../../../repository/store/store";
import { defineComponent, onBeforeMount, onBeforeUnmount, Ref, ref } from "vue";
import http from "../../../repository/http";
import { dispatchErrorBox, dispatchSuccessBox } from "../mixins";
import { dispatchError } from "../../utils/error_handling";

const __default__ = defineComponent({
  name: "register-modal",
  components: { ModernInput, IdentityForm, InputPassword },

  setup(_, { emit }) {
    /** Referes the new user that will be registered */
    const user = ref(new User());

    /** Contain all of Brazil's states */
    const states = ref([{ stateID: null, id: null, label: "Selecionar" }]);

    /** Contain the max date value that can be applied in the date input example 2022-06-08 */
    const maxDate: Ref<string> = ref("");

    /** Variable that makes the password visible. */
    const isPasswordVisible: Ref<boolean> = ref(false);

    /** Used to control the label state */
    const isInputFocused: Ref<boolean> = ref(false);

    onBeforeMount(async () => {
      await loadStates();
      calcMaxDate();
    });

    onBeforeUnmount(async () => {
      user.value = new User();
    });

    /** Calculate the max value that the date input can contain */
    const calcMaxDate = () => {
      const currentDate = new Date();
      let day: string | number = currentDate.getDay();
      let month: string | number = currentDate.getMonth();
      const year = currentDate.getFullYear();
      day = formatDayMonth(day.toString());
      month = formatDayMonth(month.toString());
      maxDate.value = year + "-" + month + "-" + day;
    };

    /** Verifies if day or month has only one character, if true put 0 in end
     * @param value might be the day or the month, example day "4" or month "12". */
    const formatDayMonth = (value: string): string => {
      return value.length == 1 ? "0" + value : value;
    };

    /** Makes a request to get all the states of Brazil. */
    async function loadStates() {
      try {
        const response = await http.get("api/decola/states");
        states.value = [...states.value, ...response.data.states];
      } catch (e) {
        console.log(e);
      }
    }

    /** Used to display some information while the address is loading. */
    function fillAddressLoading() {
      const loading = "...";
      user.value.address.federalUnit = loading;
      user.value.address.city = loading;
      user.value.address.district = loading;
      user.value.address.street = loading;
    }

    /** To clear all address fields. Usually used when the provided cep does not exist. */
    function clearFillAddress() {
      user.value.address.zipCode = "";
      user.value.address.federalUnit = "";
      user.value.address.city = "";
      user.value.address.district = "";
      user.value.address.street = "";
    }

    /** Used to fill the address field with the payload received from the backend app.
     * @see Address */
    function fillAddress(address: Address) {
      user.value.address = address;
    }

    /** Request the address by passing the cep.
     * @param cep follow this pattern "89110110" */
    async function requestCepAPI(cep: string): Promise<void> {
      if (cep.length < 9) {
        user.value.address.zipCode = cep;
        return;
      }
      try {
        fillAddressLoading();
        const response = await http.get(`/api/decola/cep/${cep}`);
        const address: Address = {
          ...response.data,
          zipCode: user.value.address.zipCode
        };
        fillAddress(address);
      } catch (e) {
        clearFillAddress();
        console.log(e);
        await dispatchErrorBox("O CEP digitado não existe!");
      }
    }

    /** Undo the structural changes applied by the input's mask to send correctly to the backend app.*/
    const formatUserData = () => {
      user.value.document = user.value.document
        .replaceAll("-", "")
        .replaceAll(".", "");
      user.value.address.zipCode = user.value.address.zipCode.replace("-", "");
      user.value.address.stateID = Number(user.value.address.stateID);
      user.value.address.addressNumber = Number(
        user.value.address.addressNumber
      );
    };

    /** Register a new user with the information provided through the form. */
    async function register(): Promise<void> {
      try {
        formatUserData();
        await store.dispatch(
          "authentication_module/registerNewUser",
          user.value
        );
        await dispatchSuccessBox("Cadastro realizado com sucesso!");
        emit("toggleRegisterModal");
      } catch (e) {
        await dispatchError(e);
      }
    }

    /** Function that makes the password visible or not. */
    function togglePassword() {
      isPasswordVisible.value = !isPasswordVisible.value;
    }

    const setForeigner = (input: HTMLInputElement): void => {
      user.value.isForeigner = input.checked;
      if (user.value.isForeigner) {
        user.value.document = "";
      }
    };

    function maskDate(input: string) {
      let inputElement = document.getElementById(
        "input-date"
      ) as HTMLInputElement;

      if (input.match(/^\d{2}$/)) {
        inputElement.value = input + "/";
      } else if (input.match(/^\d{2}\/\d{2}$/)) {
        inputElement.value = input + "/";
      }
    }

    /** This will be called when the input receive and event of type focus or blur and will be used to control the label state
     * @param value Refers to the current content_provider coming from the input.
     * @param isFocused Receive true when the event type is focus and false when the event type is blur. */
    const toggleLabelClass = (value, isFocused: boolean): void => {
      isInputFocused.value = isFocused;
      if (value && !isFocused) {
        isInputFocused.value = true;
      }
    };

    const onInput = async (event: Event) => {
      const input = event.target as HTMLInputElement;

      if (!input.value || input.value === "") {
        return;
      }
      maskDate(input.value);
      user.value.birthDate = input.value
        .split("/")
        .reverse()
        .join("-");
    };

    return {
      user,
      states,
      maxDate,
      isPasswordVisible,
      isInputFocused,
      toggleLabelClass,
      setForeigner,
      requestCepAPI,
      register,
      togglePassword,
      maskDate,
      onInput
    };
  }
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "1cb6553e": (_ctx.color)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__